import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Select from '@tds/core-select';
import Input from '@tds/core-input';
import TextArea from '@tds/core-text-area';
import Button from '@tds/core-button';

import { Box } from '../design/css';
import request, { USER_RIGHTS } from '../utils/request';
import getStatusOptions from './statusOptions';
import ValidateLoader from '../UI/validateLoader';
import MessageNotification from '../messageNotification';
import TypeAheadFilter from '../design/shared/filter/typeAheadFilter';
import { getFormattedDateUTC } from '../utils/utils';

const UpdateOrder = ({
  order,
  setOpen,
  open,
  onSave,
  users,
  assignedUserId,
  isOptOrder,
  staticLabels,
  children
}) => {
  const locale = localStorage.getItem('locale');
  const [ecid, setEcid] = useState(order.ecid);
  const [status, setStatus] = useState(order.status);
  const [installationDate, setInstallationDate] = useState(
    getFormattedDateUTC(order.installation_date)
  );
  const [comment, setComment] = useState();
  const [loading, setloading] = useState();
  const [error, setError] = useState();
  const [defaultAssignee, setDefaultAssignee] = useState([]);
  const [assignee, setAssignee] = useState(defaultAssignee);
  const profile = request.getProfile();
  const userRoleId = profile && profile.role.id;
  const userRole = profile && profile.role.name;
  const userRights = userRole && USER_RIGHTS[userRole];
  const isChannelTQPartenaire = profile.channelId === 10;
  const isStatusChanged = status !== order.status;
  const isInstallationDateChanged =
    installationDate !== getFormattedDateUTC(order.installation_date);
  const isAssigneeUpdated = defaultAssignee !== assignee;
  const isEcidChanged = ecid !== order.ecid;
  const isOrderUpdated =
    isStatusChanged ||
    isInstallationDateChanged ||
    comment ||
    isAssigneeUpdated ||
    isEcidChanged;
  const { REASSIGN } = staticLabels;

  // TQ Partenaire sales agents and managers have the ability to update the order status
  // to Responded or Cancelled
  if ([2, 3].includes(userRoleId) && isChannelTQPartenaire) {
    userRights.isStatusEditable = true;
    userRights.isChannelTQPartenaire = true;
  }

  useEffect(() => {
    if (users && assignedUserId && !assignee.length) {
      const currentAssignee = users.filter(user => user.id === assignedUserId);
      setDefaultAssignee(currentAssignee);
      setAssignee(currentAssignee);
    }
  }, [assignedUserId, users]);

  const getAssigneeId = () => {
    if (!isAssigneeUpdated) return undefined;
    return assignee.length ? assignee[0].id : -1;
  };

  const addComment = () => {
    const commentsBody = {
      comment
    };
    return request.post(`/orders/${order.id}/comments`, commentsBody);
  };

  const updateOrder = assignedId => {
    setloading(true);
    const updateBody = {
      ecid,
      status,
      installationDate,
      assigned_user_id: assignedId || getAssigneeId()
    };

    return request.put(`/orders/${order.id}`, updateBody);
  };

  const onEcidChange = ({ target }) => {
    setEcid(target.value);
  };

  const onStatusChange = ({ target }) => {
    setStatus(target.value);
  };

  const onInstallationDateChange = ({ target }) => {
    setInstallationDate(target.value);
  };

  const closeModal = () => {
    if (isOrderUpdated && !error) {
      onSave({ isOrderUpdated: true, orderId: order.order_id });
      setOpen(!open);
    }
  };

  const onClickSave = async () => {
    if (!isOrderUpdated) return;
    try {
      setloading(true);
      if (
        isInstallationDateChanged ||
        isStatusChanged ||
        isAssigneeUpdated ||
        isEcidChanged
      ) {
        await updateOrder();
      }
      if (comment) {
        await addComment();
      }
      closeModal();
    } catch (err) {
      setError(err.message);
    }
    setloading(false);
  };

  const onClickCancel = () => {
    setOpen(null);
  };

  const onClickReAssign = async () => {
    const id = order.previous_assigned_user_id || -1;
    await updateOrder(id);
    onSave({ isOrderUpdated: true, orderId: order.order_id });
    setOpen(!open);
  };

  const buttons = [
    {
      label: locale === 'FR' ? 'Annuler' : 'Cancel',
      onClick: onClickCancel,
      variant: 'primary'
    },
    {
      label: locale === 'FR' ? 'Sauver' : 'Save',
      onClick: onClickSave,
      variant: 'secondary'
    }
  ];

  return (
    <>
      {loading && (
        <ValidateLoader
          message={
            locale === 'FR' ? 'Chargement du contenu...' : 'Loading Content....'
          }
        />
      )}
      {userRights && userRights.canAssignOrder && !isOptOrder && (
        <Box display="flex" width={30} pt={24} pb={24}>
          <TypeAheadFilter
            label={locale === 'FR' ? 'Attribuer' : 'Assign To:'}
            onChange={value => setAssignee(value)}
            options={users}
            selected={assignee}
            placeholder={locale === 'FR' ? 'Non attribué' : 'Unassigned'}
          />
        </Box>
      )}
      <Box>{children}</Box>
      {error && <MessageNotification variant="error" message={error} />}
      <Box display="flex" width={50} mdWidth={100}>
        {order.value_chain === 'ADT_CONS' && (
          <Box width={100}>
            <Input
              label="ECID"
              defaultValue={ecid}
              readOnly={userRights && !userRights.isEcidEditable}
              onChange={onEcidChange}
            />
          </Box>
        )}
        {userRights && userRights.isStatusEditable && !isOptOrder && (
          <Box width={100}>
            <Select
              label={locale === 'FR' ? 'État de mise à jour' : 'Update Status:'}
              placeholder={locale === 'FR' ? 'État: ' : 'Status: '}
              options={getStatusOptions(
                userRole,
                order.status,
                userRights.isChannelTQPartenaire
              )}
              onChange={onStatusChange}
              defaultValue={status}
            />
          </Box>
        )}
        {!isOptOrder && (
          <Box width={100}>
            <Input
              label={
                locale === 'FR' ? "Date d'installation" : 'Installation date:'
              }
              type="date"
              defaultValue={installationDate}
              readOnly={userRights && !userRights.isInstallationDateEditable}
              onChange={onInstallationDateChange}
            />
          </Box>
        )}
        <Box width={100}>
          <TextArea
            label={locale === 'FR' ? 'Ajouter un commentaire' : 'Add a comment'}
            id="order-validation"
            error={
              locale === 'FR'
                ? 'Le champ doit être rempli.'
                : 'Field must be filled.'
            }
            onChange={event => setComment(event.target.value)}
          />
        </Box>
      </Box>
      <Box
        display="flex"
        justifyContent="space-between"
        flexDirection="row"
        width={50}
        mdWidth={100}
        pr={16}
        pl={16}
        pt={28}
        pb={24}
      >
        {userRights &&
          userRights.canAssignOrderBack &&
          assignedUserId === profile.id &&
          !isOptOrder && (
            <Button variant="secondary" onClick={onClickReAssign}>
              {REASSIGN}
            </Button>
          )}
        {buttons.map((item, idx) => {
          const { label, onClick, variant } = item;
          const key = `button${idx}`;

          return (
            <Button key={key} variant={variant} onClick={onClick}>
              {label}
            </Button>
          );
        })}
      </Box>
    </>
  );
};

UpdateOrder.propTypes = {
  order: PropTypes.shape({
    id: PropTypes.number,
    order_id: PropTypes.string,
    status: PropTypes.string,
    installation_date: PropTypes.string,
    ecid: PropTypes.string,
    value_chain: PropTypes.string,
    previous_assigned_user_id: PropTypes.number
  }).isRequired,
  setOpen: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  users: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number
    })
  ).isRequired,
  assignedUserId: PropTypes.number,
  isOptOrder: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  staticLabels: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired
};

UpdateOrder.defaultProps = {
  assignedUserId: null
};

export default UpdateOrder;
